import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { Grid, Row, Col, Glyphicon, Button, Modal, Radio, DropdownButton, MenuItem } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import EventSystem from './EventSystem';
import GlobalFunction from './globalFunction';
import RmToolTip from './rmToolTip';
import { Tile, Title, Body, LoggingIndicator } from './style/tile.style';
import '../node_modules/react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import './css/timerControlModule.css';

export default function TimerControlModule({ parameters, onSendMessage, enableModuleLogging, addLogEntry }) {
    const [timerParams, setTimerParams] = useState({ description: "", instructions: "", secs: "", toggle: false });
    const [showModal, setShowModal] = useState(false);
    const [editDisabled, setEditDisabled] = useState(true);
    const [glyphsDelEditVisible, setGlyphsDelEditVisible] = useState(false);
    const [glyphsCancelVisible, setGlyphsCancelVisible] = useState(false);
    const [glyphsSaveVisible, setGlyphsSaveVisible] = useState(false);
    const [glyphsAddVisible, setGlyphsAddVisible] = useState(true);
    const [selectedFixedTimers, setSelectedFixedTimers] = useState([]);
    const [fixedTimerActivityLog, setFixedTimerActivityLog] = useState([]);
    const [other_config, setOtherConfig] = useState({ autoHideSeconds: 0, autoHide: false });
    const [fixedTimers, setFixedTimers] = useState([]);
    const [hour, setHour] = useState("");
    const [minute, setMinute] = useState("");
    const [second, setSecond] = useState("");
    const [duration, setDuration] = useState("");
    const [startMessage, setStartMessage] = useState("");
    const [endMessage, setEndMessage] = useState("");
    const [parentRow, setParentRow] = useState(0);
    const [childRow, setChildRow] = useState(0);
    const [toggle, setToggle] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [timerExplanation, setTimerExplanation] = useState("");
    const [timerRuning, setTimerRuning] = useState(false);
    const [clockLocalOffset] = useState(0);
    const [startButtonFontSize, setStartButtonFontSize] = useState(14);
    const [timerType, setTimerType] = useState('select');
    const [dropdownTimers, setDropdownTimers] = useState([]);
    const [dropdownTitle, setDropdownTitle] = useState('Select Timer');
    const [chainSets, setChainSets] = useState([]);
    const [headingFontSize, setHeadingFontSize] = useState(1);
    const [dataRow, setDataRow] = useState({ height: 0, fontSize: 0, gap: 0, col1: "", col2: "" });
    const [multipleTImerButtonsForDisplay, setMultipleTImerButtonsForDisplay] = useState([]);
    const [timerButtons, setTimerButtons] = useState([]);
    const [loggingModule, setLoggingModule] = useState(false);
    const [highlight, setHighlight] = useState(false);
    const [scheduledTimers, setScheduledTimers] = useState([]);
    const [numberWidth, setNumberWidth] = useState(0);

    const intervalIDRef = useRef(undefined);
    const timerIDRef = useRef(undefined);
    const errorTimerIDRef = useRef(undefined);
    const scheduledTimersRef = useRef([]);
    scheduledTimersRef.current = scheduledTimers;
    const otherConfigRef = useRef(other_config);
    otherConfigRef.current = other_config;
    const mountedRef = useRef(false);
    const firstTimeRunRef = useRef(true);

    const fontSizeCheckerRef = useRef(null);
    const containerRef = useRef(null);
    const fixedTimerHeadingRowRef = useRef(null);

    const liveRef = useRef({ parameters, onSendMessage, addLogEntry });
    liveRef.current.parameters = parameters;
    liveRef.current.onSendMessage = onSendMessage;
    liveRef.current.addLogEntry = addLogEntry;

    const timerStateChangedStable = useRef(function(state) {
        if (!mountedRef.current) {
            console.log('Timer not mounted in timerStateChange');
            return;
        }
        setTimerRuning(state === 'start');
    });
    const logStatusChangeStable = useRef(function(data) { setLoggingModule(data); });
    const highlightStatusStable = useRef(function(status) { setHighlight(status); });

    useEffect(() => {
        mountedRef.current = true;

        EventSystem.subscribe('TimerDisplayModule', timerStateChangedStable.current);
        EventSystem.subscribe('logStatus' + parameters.config.event_screen_module_id, logStatusChangeStable.current);
        EventSystem.subscribe('highlightModule' + parameters.config.event_screen_module_id, highlightStatusStable.current);

        enableModuleLogging();
        updateDimensions();
        window.addEventListener("resize", updateDimensions);

        const savedTimerParams = JSON.parse(localStorage.getItem(
            parameters.config.module_name + '_' + parameters.config.event_screen_module_id
        ));
        if (savedTimerParams !== null) {
            setTimerParams(savedTimerParams);
        }

        if (!Object.is(parameters.config.other_config, null)) {
            if (Object.entries(parameters.config.other_config).length === 2) {
                setOtherConfig(parameters.config.other_config);
            }
        }

        const fixedTimerSaved = JSON.parse(localStorage.getItem(
            parameters.config.module_name + '_fixed_' + parameters.config.event_screen_module_id
        ));
        if (fixedTimerSaved !== null && fixedTimerSaved.data.length > 0) {
            setTimerType(fixedTimerSaved.type);
            setFixedTimers(fixedTimerSaved.data);
            if (fixedTimerSaved.type === 'scheduled') {
                loadSchedule(fixedTimerSaved.data);
            }
            if (fixedTimerSaved.type === 'multiple') {
                reSequenceTimers(fixedTimerSaved.data);
                buildChainSets(fixedTimerSaved.data);
            }
        }

        return () => {
            mountedRef.current = false;
            EventSystem.unsubscribe('TimerDisplayModule', timerStateChangedStable.current);
            EventSystem.unsubscribe('logStatus' + parameters.config.event_screen_module_id, logStatusChangeStable.current);
            EventSystem.unsubscribe('localClockOffset');
            EventSystem.unsubscribe('highlightModule' + parameters.config.event_screen_module_id, highlightStatusStable.current);
            if (typeof intervalIDRef.current !== 'undefined') {
                clearInterval(intervalIDRef.current);
            }
            window.removeEventListener("resize", updateDimensions);
        };
    }, []);

    function updateDimensions() {
        const p = liveRef.current.parameters;
        const headingWidth = containerRef.current.clientWidth - 80;

        let fontSize = GlobalFunction.getFontSize(p.config.description, headingWidth, 37, fontSizeCheckerRef.current);
        if (fontSize > 22) fontSize = 22;

        const nw = GlobalFunction.getTextWidth("00", fontSize, fontSizeCheckerRef.current);
        const dataHeight = p.containerHeight.container - 55;

        const dr = {};
        dr.height = dataHeight / 3 < 34 ? Math.floor(dataHeight / 3) : 34;
        dr.fontSize = Math.min(Math.floor(dr.height * .6), 14);
        dr.gap = (dataHeight - (3 * dr.height)) / 3;

        if (containerRef.current.clientWidth < 200) {
            dr.col1 = 'col-sm-8'; dr.col2 = 'col-sm-4'; dr.padding = "10px"; dr.buttonRatio = .75;
        } else {
            dr.col1 = 'col-sm-9'; dr.col2 = 'col-sm-3'; dr.padding = "15px"; dr.buttonRatio = .66;
        }

        const btnWidth = ((containerRef.current.clientWidth * dr.buttonRatio) - 20) * .8;
        let sfSize = GlobalFunction.getFontSize('Start Timer', btnWidth, dr.height, fontSizeCheckerRef.current);
        if (sfSize > 14) sfSize = 14;

        setDataRow(dr);
        setHeadingFontSize(fontSize);
        setStartButtonFontSize(sfSize);
        setNumberWidth(nw);
    }

    function getEventTime() {
        const p = liveRef.current.parameters;
        return new Date(Date.now() + p.config.clockOffset - p.config.timeMasterClockOffset);
    }

    function addToLog(activity) {
        const utcTime = getEventTime();
        setFixedTimerActivityLog(prev => {
            const len = prev.length;
            if (len > 0 && prev[len - 1].activity === activity) return prev;
            return [...prev, { utcTime, row: len, activity }];
        });
    }

    function convertFixedToSchedule(item) {
        let runTime = moment().hour(parseInt(item.hour, 10)).minute(parseInt(item.minute, 10)).second(parseInt(item.second, 10));
        if (runTime < moment()) runTime = moment(runTime).add(1, 'day');
        return {
            runTime,
            duration: parseInt(item.duration, 10),
            startMessage: item.startMessage,
            endMessage: item.endMessage,
            toggle: item.toggle
        };
    }

    function loadSchedule(timers) {
        let arr = [];
        for (let x = 0; x < timers.length; x++) {
            if (timers[x].hour !== "*") {
                arr.push(convertFixedToSchedule(timers[x]));
            } else {
                const ht = {
                    minute: timers[x].minute, second: timers[x].second,
                    duration: timers[x].duration, startMessage: timers[x].startMessage,
                    endMessage: timers[x].endMessage, toggle: timers[x].toggle
                };
                for (let hh = 0; hh < 24; hh++) {
                    arr.push(convertFixedToSchedule({ ...ht, hour: hh }));
                }
            }
        }
        if (arr.length > 1) arr.sort((a, b) => a.runTime - b.runTime);
        scheduledTimersRef.current = arr;
        setScheduledTimers(arr);
        scheduler(arr);
    }

    function scheduler(initialTimers) {
        addToLog('Schedule reloaded');
        addToLog('Next: ' + moment(initialTimers[0].runTime).format('H:mm:ss') + ', ' +
            initialTimers[0].duration + ' secs - ' + initialTimers[0].startMessage);

        intervalIDRef.current = setInterval(() => {
            const current = scheduledTimersRef.current;
            if (current.length > 0) {
                const currentEventDate = getEventTime();
                const diff = Math.abs(currentEventDate - current[0].runTime);
                if (diff < 120401 && diff > 120000) {
                    addToLog('In 2 mins: ' + moment(current[0].runTime).format('H:mm:ss') + ', ' +
                        current[0].duration + ' secs - ' + current[0].startMessage);
                }
                if (currentEventDate - current[0].runTime > -100) {
                    const p = liveRef.current.parameters;
                    const msg = {
                        sourceModuleName: p.config.module_name,
                        event_grouping_seq: p.config.event_grouping_seq,
                        customerID: p.userProfile.customer_id,
                        siteID: p.userProfile.site_id,
                        eventID: p.config.event_id,
                        timerParams: {
                            description: current[0].startMessage,
                            instructions: current[0].endMessage,
                            secs: current[0].duration,
                            toggle: current[0].toggle,
                            startTimeAtOrigin: getEventTime().valueOf()
                        },
                        flash: true,
                        seconds: parseInt(p.config.seconds, 10),
                        other_config: otherConfigRef.current,
                        action: 'start'
                    };
                    liveRef.current.onSendMessage(msg);
                    addToLog('Running: ' + current[0].duration + ' secs - ' + current[0].startMessage + ', ' + moment(current[0].runTime).format('H:mm:ss'));
                    const remaining = current.slice(1);
                    scheduledTimersRef.current = remaining;
                    setScheduledTimers(remaining);
                    if (remaining.length > 0) {
                        addToLog('Next: ' + moment(remaining[0].runTime).format('H:mm:ss') + ', ' +
                            remaining[0].duration + ' secs - ' + remaining[0].startMessage);
                    }
                }
            }
        }, 100);
    }

    function startTimer(waitTime, sequence) {
        timerIDRef.current = setTimeout(() => {
            const timerDetail = sequence[0];
            const p = liveRef.current.parameters;
            const msg = {
                sourceModuleName: p.config.module_name,
                event_grouping_seq: p.config.event_grouping_seq,
                customerID: p.userProfile.customer_id,
                siteID: p.userProfile.site_id,
                eventID: p.config.event_id,
                timerParams: {
                    description: timerDetail.startMessage,
                    instructions: timerDetail.endMessage,
                    secs: timerDetail.duration,
                    toggle: timerDetail.toggle,
                    startTimeAtOrigin: getEventTime().valueOf()
                },
                flash: true,
                seconds: parseInt(p.config.seconds, 10),
                other_config: otherConfigRef.current,
                action: 'start'
            };
            liveRef.current.onSendMessage(msg);

            if (sequence.length > 1) {
                const currentTimerDuration = timerDetail.duration;
                const newSeq = sequence.slice(1);
                setMultipleTimerSequence(newSeq);
                startTimer(currentTimerDuration, newSeq);
            }
        }, waitTime * 1000);
    }

    function startMultipleTimer(e) {
        const timerButtonRow = parseInt(e.target.attributes.name.value, 10);
        const seq = timerButtons[timerButtonRow];
        setMultipleTimerSequence(seq);
        startTimer(0, seq);
    }

    function killTimer() {
        handleClick({ target: { name: "kill" } });
    }

    function handleClick(e) {
        const p = parameters;
        const msg = {
            sourceModuleName: p.config.module_name,
            event_grouping_seq: p.config.event_grouping_seq,
            customerID: p.userProfile.customer_id,
            siteID: p.userProfile.site_id,
            eventID: p.config.event_id
        };
        if (e.target.name === 'kill') {
            msg.action = 'kill';
        } else {
            if (timerRuning) {
                onSendMessage({ ...msg, action: 'kill' });
            }
            msg.timerParams = { ...timerParams, startTimeAtOrigin: getEventTime().valueOf() };
            msg.flash = true;
            msg.seconds = parseInt(p.config.seconds, 10);
            msg.other_config = other_config;
            msg.action = 'start';
        }
        onSendMessage(msg);
        addLogEntry('Clock offset for server synchronisaiton: ' + p.config.clockOffset);
        addLogEntry('Timemaster offset: ' + p.config.timeMasterClockOffset);
        addLogEntry('Local offset: ' + clockLocalOffset);
    }

    function handleChange(e) {
        setTimerParams(prev => {
            let next;
            if (e.target.name === 'toggle') {
                next = { ...prev, toggle: !prev.toggle };
            } else if (e.target.name === 'secs') {
                next = { ...prev, secs: !Number.isNaN(parseInt(e.target.value, 10)) ? parseInt(e.target.value, 10) : 0 };
            } else {
                next = { ...prev, [e.target.name]: e.target.value };
            }
            localStorage.setItem(
                parameters.config.module_name + '_' + parameters.config.event_screen_module_id,
                JSON.stringify(next)
            );
            return next;
        });
    }

    function handleTimerKeyDown(e) {
        if (e.key === 'Enter' && !startButtonDisabled) {
            e.preventDefault();
            handleClick({ target: { name: 'start' } });
        }
    }

    function handleButttonClick(e) {
        if (e.target.name === 'cancel') {
            setShowModal(false);
            if (fixedTimers.length === 0) {
                setTimerType('select');
            } else if (timerType === 'scheduled') {
                loadSchedule(fixedTimers);
            } else if (timerType === 'multiple') {
                formatButtonsForController(timerButtons);
            }
        }
    }

    function clearEditRow() {
        setHour(""); setMinute(""); setSecond(""); setDuration("");
        setStartMessage(""); setEndMessage(""); setToggle(false);
        setChildRow(0); setParentRow(0); setDropdownTitle('Select Timer');
    }

    function editFixedTimer(row, isSelected) {
        if (isSelected) {
            setSelectedFixedTimers([row.row]);
            setHour(row.hour); setMinute(row.minute); setSecond(row.second);
            setDuration(row.duration); setToggle(row.toggle);
            setChildRow(row.childRow);
            setParentRow(row.parentRow);
            setStartMessage(row.startMessage);
            setEndMessage(row.endMessage);
            loadChainDropdown(row.childRow, [row.row]);
            if (row.childRow !== 0) {
                const idx = fixedTimers.findIndex(r => r.row === row.childRow);
                setDropdownTitle(fixedTimers[idx].startMessage);
            } else {
                setDropdownTitle('Select Timer');
            }
            setGlyphsDelEditVisible(true);
            setGlyphsSaveVisible(false);
            setGlyphsCancelVisible(false);
            setGlyphsAddVisible(false);
        } else {
            setSelectedFixedTimers([]);
            clearEditRow();
            setGlyphsDelEditVisible(false);
            setGlyphsSaveVisible(false);
            setGlyphsCancelVisible(false);
            setEditDisabled(true);
            setTimerExplanation("");
            setGlyphsAddVisible(true);
        }
    }

    function validNumber(dataField, maxNumber, fieldUnit, fieldLabel) {
        const errorMsg = fieldLabel + " must be a number between " + (fieldLabel === "Duration" ? "1" : "0") + " and " + maxNumber + ' ' + fieldUnit;
        const minAmount = fieldLabel === "Duration" ? 1 : 0;
        if (Number.isNaN(Number.parseInt(dataField, 10))) { showErrorMessage(errorMsg); return false; }
        if (Number.parseInt(dataField, 10) > maxNumber || Number.parseInt(dataField, 10) < minAmount) { showErrorMessage(errorMsg); return false; }
        return true;
    }

    function handleFixedChange(e) {
        if (e.target.name === "hour") {
            if (e.target.value.length === 0 || e.target.value === "*") {
                setHour(e.target.value); clearErrorMessage(); return;
            }
            if (validNumber(e.target.value, 23, "hours", "Hours")) { setHour(Number.parseInt(e.target.value, 10)); clearErrorMessage(); }
        } else if (e.target.name === "minute") {
            if (e.target.value === "") { setMinute(e.target.value); return; }
            if (validNumber(e.target.value, 59, "minutes", "Minutes")) { setMinute(Number.parseInt(e.target.value, 10)); clearErrorMessage(); }
        } else if (e.target.name === "second") {
            if (e.target.value === "") { setSecond(e.target.value); return; }
            if (validNumber(e.target.value, 59, "seconds", "Seconds")) { setSecond(Number.parseInt(e.target.value, 10)); clearErrorMessage(); }
        } else if (e.target.name === "duration") {
            if (e.target.value === "") { setDuration(e.target.value); return; }
            if (validNumber(e.target.value, 3600, "seconds", "Duration")) { setDuration(Number.parseInt(e.target.value, 10)); clearErrorMessage(); }
        } else if (e.target.name === "startMessage" || e.target.name === "endMessage") {
            e.target.name === "startMessage" ? setStartMessage(e.target.value) : setEndMessage(e.target.value);
            clearErrorMessage();
        } else if (e.target.name === "toggle") {
            setToggle(prev => !prev); clearErrorMessage();
        }
    }

    function handleFixedChangeBlur() {
        setHour(prev => prev.length === 0 ? "*" : prev);
    }

    function showErrorMessage(message) {
        if (errorTimerIDRef.current) clearTimeout(errorTimerIDRef.current);
        setErrorMessage(message);
        errorTimerIDRef.current = setTimeout(() => setErrorMessage(""), 3000);
    }

    function clearErrorMessage() {
        clearTimeout(errorTimerIDRef.current);
        setErrorMessage("");
        setGlyphsSaveVisible(true);
    }

    function saveFixedTimersToLocalStorage(timerTypeVal, timers) {
        localStorage.setItem(
            parameters.config.module_name + '_fixed_' + parameters.config.event_screen_module_id,
            JSON.stringify({ type: timerTypeVal, data: timers })
        );
    }

    function buildChainSets(timers) {
        const newChainSets = [];
        function addToChainSet(existingChainMember, newChainMember) {
            let notFound = true;
            for (let x = 0; x < newChainSets.length; x++) {
                if (newChainSets[x].has(existingChainMember)) {
                    newChainSets[x].add(newChainMember);
                    notFound = false;
                }
            }
            if (notFound) {
                const s = new Set();
                s.add(newChainMember);
                newChainSets.push(s);
            }
        }
        for (let x = 0; x < timers.length; x++) {
            if (timers[x].childRow > 0) addToChainSet(timers[x].childRow, timers[x].row);
            if (timers[x].parentRow > 0) addToChainSet(timers[x].parentRow, timers[x].row);
        }
        setChainSets(newChainSets);
        return newChainSets;
    }

    function notInOurChainset(us, potentialMember) {
        for (let x = 0; x < chainSets.length; x++) {
            if (chainSets[x].has(us) && chainSets[x].has(potentialMember)) return false;
        }
        return true;
    }

    function loadChainDropdown(currentChildRow, currentSelectedFixedTimers) {
        const items = [];
        if (currentChildRow !== 0) {
            items.push(<MenuItem key={currentChildRow} eventKey={currentChildRow}>Remove Link</MenuItem>);
        }
        const selectedRow = currentSelectedFixedTimers && currentSelectedFixedTimers.length > 0 ? currentSelectedFixedTimers[0] : 0;
        for (let x = 0; x < fixedTimers.length; x++) {
            if (fixedTimers[x].parentRow === 0) {
                if (notInOurChainset(selectedRow, fixedTimers[x].row) && fixedTimers[x].row !== selectedRow) {
                    if (currentChildRow !== 0 && items.length === 1) {
                        items.push(<MenuItem divider key="divider" />);
                    }
                    items.push(<MenuItem key={fixedTimers[x].row} eventKey={fixedTimers[x].row}>{fixedTimers[x].startMessage}</MenuItem>);
                }
            }
        }
        setDropdownTimers(items);
    }

    function reSequenceTimers(timers) {
        setFixedTimerActivityLog([]);
        const newTimers = [];
        const newTimerButtons = [];
        for (let x = 0; x < timers.length; x++) {
            if (timers[x].parentRow === 0) {
                newTimers.push(timers[x]);
                const timerGroups = [timers[x]];
                let childToFind = timers[x].childRow;
                while (childToFind !== 0) {
                    const idx = timers.findIndex(r => r.row === childToFind);
                    newTimers.push(timers[idx]);
                    timerGroups.push(timers[idx]);
                    childToFind = timers[idx].childRow;
                }
                newTimerButtons.push(timerGroups);
            }
        }
        setTimerButtons(newTimerButtons);
        setFixedTimers(newTimers);
        if (firstTimeRunRef.current) {
            firstTimeRunRef.current = false;
            formatButtonsForController(newTimerButtons);
        }
    }

    function editRow() {
        setGlyphsDelEditVisible(false);
        setGlyphsCancelVisible(true);
        setEditDisabled(false);
    }

    function deleteRow() {
        const newFixedTimers = fixedTimers
            .filter(r => r.row !== selectedFixedTimers[0])
            .map(r => {
                const copy = { ...r };
                if (copy.childRow === selectedFixedTimers[0]) copy.childRow = 0;
                if (copy.parentRow === selectedFixedTimers[0]) copy.parentRow = 0;
                return copy;
            });

        setFixedTimers(newFixedTimers);
        saveFixedTimersToLocalStorage(timerType, newFixedTimers);
        reSequenceTimers(newFixedTimers);
        buildChainSets(newFixedTimers);

        if (newFixedTimers.length === 0 && typeof intervalIDRef.current !== 'undefined') {
            clearInterval(intervalIDRef.current);
        }

        setSelectedFixedTimers([]);
        clearEditRow();
        setGlyphsDelEditVisible(false); setGlyphsSaveVisible(false); setGlyphsCancelVisible(false);
        setEditDisabled(true); setTimerExplanation(""); setGlyphsAddVisible(true);
    }

    function addRow() {
        setGlyphsAddVisible(false);
        setSelectedFixedTimers([]);
        clearEditRow();
        loadChainDropdown(0, []);
        setGlyphsDelEditVisible(false); setGlyphsSaveVisible(false); setGlyphsCancelVisible(true);
        setEditDisabled(false); setGlyphsAddVisible(false);
    }

    function saveRow() {
        let updatedExistingRow = false;
        let newFixedTimers = [];
        let highwaterRow = 0;

        for (let x = 0; x < fixedTimers.length; x++) {
            if (fixedTimers[x].row === selectedFixedTimers[0]) {
                newFixedTimers.push({ row: selectedFixedTimers[0], hour, minute, second, duration, startMessage, endMessage, toggle, parentRow, childRow });
                updatedExistingRow = true;
            } else {
                const r = { ...fixedTimers[x] };
                if (r.row === childRow) r.parentRow = selectedFixedTimers[0];
                else if (r.parentRow === selectedFixedTimers[0]) r.parentRow = 0;
                newFixedTimers.push(r);
            }
            if (fixedTimers[x].row > highwaterRow) highwaterRow = fixedTimers[x].row;
        }

        if (!updatedExistingRow) {
            const newRow = { row: highwaterRow + 1, hour, minute, second, duration, startMessage, endMessage, toggle, parentRow, childRow };
            newFixedTimers.push(newRow);
            if (childRow !== 0) {
                for (let y = 0; y < newFixedTimers.length; y++) {
                    if (newFixedTimers[y].row === childRow) newFixedTimers[y].parentRow = highwaterRow + 1;
                }
            }
        }

        setFixedTimers(newFixedTimers);
        saveFixedTimersToLocalStorage(timerType, newFixedTimers);
        reSequenceTimers(newFixedTimers);
        buildChainSets(newFixedTimers);

        setSelectedFixedTimers([]);
        clearEditRow();
        setGlyphsDelEditVisible(false); setGlyphsSaveVisible(false); setGlyphsCancelVisible(false);
        setEditDisabled(true); setTimerExplanation(""); setGlyphsAddVisible(true);
    }

    function undoRow() {
        setEditDisabled(true);
        clearEditRow();
        setGlyphsDelEditVisible(false); setGlyphsSaveVisible(false); setGlyphsCancelVisible(false);
        setSelectedFixedTimers([]); setTimerExplanation(""); setGlyphsAddVisible(true);
    }

    function showToggle(ticked) {
        return ticked ? <Glyphicon glyph="check" /> : <Glyphicon glyph="unchecked" />;
    }

    function toggleFormatter(cell, row) { return showToggle(row.toggle); }

    function startMessageFormatter(cell, row) {
        if (timerType === 'multiple') {
            const arrow = String.fromCharCode(0x21AA);
            if (row.parentRow === 0 && row.childRow !== 0) return <span style={{ color: "red" }}>{cell}</span>;
            if (row.parentRow !== 0) return <div><span style={{ color: "red" }}>{arrow}</span>{cell}</div>;
        }
        return cell;
    }

    function endMessageFormatter(cell, row) {
        if (timerType === 'multiple' && row.childRow !== 0) return <Glyphicon glyph="save" />;
        return cell;
    }

    function formatButtonsForController(btns) {
        const arrow = String.fromCharCode(0x21AA);
        const check = String.fromCharCode(0x02713);
        const rArrow = String.fromCharCode(0x021E5) + " ";
        const optionStyle = {
            borderBottom: "1px solid " + GlobalFunction.getPaletteColour(parameters.config.colour) + '66',
            paddingTop: "10px", paddingBottom: "10px", overflow: "hidden", borderRadius: 0
        };

        const display = btns.map((group, x) => {
            let dataLines = "";
            for (let y = 0; y < group.length; y++) {
                dataLines += (y > 0 ? arrow : "") + group[y].startMessage + '/' + group[y].duration + 's ' +
                    (y === group.length - 1 ? rArrow + group[y].endMessage : "") +
                    (group[y].toggle ? check : "") + '\n';
            }
            return (
                <li key={"multiple" + x} style={optionStyle}>
                    <table><tbody><tr>
                        <td>
                            <i className="material-icons" onClick={startMultipleTimer} name={x}
                                style={{ marginRight: "5px", textShadow: "none", paddingLeft: "4px", paddingRight: "4px", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}>
                                send
                            </i>
                        </td>
                        <td style={{ whiteSpace: 'pre' }}>{dataLines}</td>
                    </tr></tbody></table>
                </li>
            );
        });
        setMultipleTImerButtonsForDisplay(display);
    }

    function activityFormatter(cell, row) {
        if (timerType === 'multiple') {
            const arrow = String.fromCharCode(0x21AA);
            const check = String.fromCharCode(0x02713);
            const rArrow = String.fromCharCode(0x021E5) + " ";
            let dataLines = "";
            for (let y = 0; y < cell.length; y++) {
                dataLines += (y > 0 ? arrow : "") + cell[y].startMessage + '/' + cell[y].duration + 's ' +
                    (y === cell.length - 1 ? rArrow + cell[y].endMessage : "") + (cell[y].toggle ? check : "") + '\n';
            }
            return (
                <div>
                    <table className="multipleTimerTable"><tbody><tr>
                        <td>
                            <Button style={{ margin: "5px", textShadow: "none", width: "80px", paddingLeft: "4px", paddingRight: "4px", overflow: "hidden", textOverflow: "ellipsis" }}
                                className={`btn btn-sm btn-${parameters.config.colour}`}
                                name={row.row}
                                onClick={startMultipleTimer}>
                                {cell[0].startMessage}
                            </Button>
                        </td>
                        <td style={{ whiteSpace: 'pre' }}>{dataLines}</td>
                    </tr></tbody></table>
                </div>
            );
        }
        return cell;
    }

    function radioButtonClicked(e) { setTimerType(e.target.value); }

    function chainedTimerSelected(eventKey, event) {
        if (event.target.firstChild.data === 'Remove Link') {
            setChildRow(0); setDropdownTitle('Select Timer');
        } else {
            setChildRow(eventKey); setDropdownTitle(event.target.firstChild.data);
        }
        clearErrorMessage();
    }

    function showModalFn() {
        if (typeof intervalIDRef.current !== 'undefined') clearInterval(intervalIDRef.current);
        setShowModal(true);
    }

    // render
    const bodyHeight = parameters.containerHeight.container - 2 - 37 - 2;
    const col1 = dataRow.col1;
    const col2 = dataRow.col2;
    const padding = dataRow.padding;

    let modalHeading = 'Select Timer Type';
    if (timerType === 'scheduled') modalHeading = 'Configure Scheduled Timers';
    else if (timerType === 'multiple') modalHeading = 'Configure Multiple Timers';

    const errors = validate(timerType, hour, minute, second, duration, startMessage);
    let glyphsSaveVisibleCalc = glyphsSaveVisible;
    let message = "";

    if (errors.minute || errors.second || errors.duration || errors.startMessage) glyphsSaveVisibleCalc = false;
    message = errorMessage.length > 0 ? errorMessage : errors.message;

    const startBtnStyle = { height: dataRow.height + "px", lineHeight: dataRow.height + "px", marginLeft: padding, width: '100%', fontSize: startButtonFontSize, zIndex: 100, paddingTop: 0, paddingBottom: 0 };
    const timerHeading = parameters.config.description.length > 0 ? parameters.config.description : "Timer";

    const deleteGlyphStyle = { paddingTop: "5px", paddingBottom: "5px", paddingLeft: "0px", width: "50%", color: "red", fontSize: "20px", display: glyphsDelEditVisible ? "inline-block" : "none" };
    const editGlyphStyle = { paddingTop: "5px", paddingBottom: "5px", paddingLeft: "5px", width: "50%", color: "blue", fontSize: "20px", display: glyphsDelEditVisible ? "inline-block" : "none" };
    const saveGlyphStyle = { paddingTop: "5px", paddingBottom: "5px", paddingLeft: "5px", width: "50%", color: "green", fontSize: "22px", display: glyphsSaveVisibleCalc ? "inline-block" : "none" };
    const canGlyphStyle = { paddingTop: "5px", paddingBottom: "5px", width: "50%", color: "red", fontSize: "22px", display: glyphsCancelVisible ? "inline-block" : "none" };
    const addGlyphStyle = { paddingTop: "5px", paddingBottom: "5px", width: "50%", color: "green", fontSize: "22px", display: glyphsAddVisible ? "inline-block" : "none" };
    const toggleStyle = { paddingLeft: "15px", paddingRight: "2px", paddingTop: "5px", right: 0 };
    const inputStyle = { width: "100%", paddingLeft: "5px", paddingRight: "5px" };
    const stdTimerView = { display: fixedTimers.length > 0 ? "none" : "block" };
    const fixedTimerView = { display: fixedTimers.length > 0 ? "block" : "none" };
    const headStyle = { paddingLeft: "6px", paddingRight: "2px" };
    const headStyleScheduled = { paddingLeft: "6px", paddingRight: "2px", display: timerType === 'scheduled' ? "block" : "none" };
    const headStyleMultiple = { paddingLeft: "6px", paddingRight: "2px", display: timerType === 'multiple' ? "block" : "none" };
    const colStyle = { paddingLeft: "2px", paddingRight: "2px" };
    const colActionStyle = { paddingLeft: "0px", paddingRight: "0px" };
    const colStyleScheduled = { paddingLeft: "2px", paddingRight: "2px", display: timerType === 'scheduled' ? "block" : "none" };
    const colStyleMultiple = { paddingLeft: "2px", paddingRight: "2px", display: timerType === 'multiple' ? "block" : "none" };
    const inoutStyle = { height: dataRow.height + "px", lineHeight: dataRow.height + "px", fontSize: dataRow.fontSize + "px", paddingLeft: "6px", paddingRight: "6px", paddingTop: 0, paddingBottom: 0 };

    let timerLength = 0;
    if (!Number.isNaN(parseInt(timerParams.secs, 10))) timerLength = parseInt(timerParams.secs, 10);
    const startButtonDisabled = timerLength === 0 || timerParams.description.length === 0;

    const selectAvailableRowProp = {
        mode: 'radio', clickToSelect: true, bgColor: 'yellow', hideSelectColumn: true,
        onSelect: editFixedTimer, selected: selectedFixedTimers
    };

    let errorsCalc = { ...errors };
    if (editDisabled) { errorsCalc.minute = false; errorsCalc.second = false; errorsCalc.duration = false; errorsCalc.startMessage = false; message = ""; }

    const clearStyle = { position: "absolute", display: "inline-block", width: "35px", top: "0px", right: "35px", height: "30px", paddingTop: "7px", fontSize: "22px", visibility: !timerRuning ? "hidden" : "" };
    const timeGlyphStyle = { position: "absolute", top: "0", right: 0, height: "30px", width: "35px", paddingTop: "7px", fontSize: "22px" };

    const descriptionFieldStyle = {
        flexGrow: 7, marginLeft: "10px", height: dataRow.height + "px", lineHeight: dataRow.height + "px", paddingTop: "3%",
        backgroundColor: parameters.colours.backgroundColor, color: parameters.colours.color, borderStyle: "none",
        borderRadius: 0, width: "20%", borderBottom: "1px solid " + GlobalFunction.getPaletteColour(parameters.config.colour) + '66'
    };
    const secondsFieldStyle = {
        flexGrow: 1, marginLeft: "10px", marginRight: "10px", height: dataRow.height + "px", lineHeight: dataRow.height + "px", paddingTop: "3%",
        backgroundColor: parameters.colours.backgroundColor, color: parameters.colours.color, borderStyle: "none",
        borderRadius: 0, width: "30px", borderBottom: "1px solid " + GlobalFunction.getPaletteColour(parameters.config.colour) + '66'
    };
    const instructionFieldStyle = {
        flexGrow: 7, marginLeft: "10px", height: dataRow.height + "px", lineHeight: dataRow.height + "px", paddingTop: "3%",
        backgroundColor: parameters.colours.backgroundColor, color: parameters.colours.color, borderStyle: "none",
        borderBottom: "1px solid " + GlobalFunction.getPaletteColour(parameters.config.colour) + '66', borderRadius: 0, width: "20%"
    };
    const sendStyle = {
        fontSize: dataRow.fontSize * 1.4 + "px", flexGrow: 0, marginLeft: "10px", marginRight: "10px",
        cursor: "pointer", height: "100%", lineHeight: dataRow.fontSize + "px", alignSelf: "center"
    };

    const optionStyle = { borderBottom: "1px solid " + GlobalFunction.getPaletteColour(parameters.config.colour) + '66', borderRadius: 0 };
    let options = [];
    if (fixedTimerActivityLog.length > 0) {
        if (timerType === 'multiple') {
            options = multipleTImerButtonsForDisplay;
        } else {
            for (let y = fixedTimerActivityLog.length - 1; y !== 0; y--) {
                options.push(<li key={"Option" + y} style={optionStyle}>{fixedTimerActivityLog[y].activity}</li>);
            }
        }
    }

    return (
        <Tile ref={containerRef} highlight={highlight} height={parameters.containerHeight.container - 2 + "px"}>
            <Modal show={showModal} autoFocus>
                <Modal.Header>
                    <Modal.Title>{modalHeading}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Grid>
                        <Row style={{ display: timerType === 'select' ? "block" : "none" }}>
                            <Col xs={12} sm={9} md={7} lg={6}>
                                <b>Scheduled timers</b> are those that will kick off automatically based on the time that you wish
                                timer to run. They can either be one off timers, ie that run at a specific hour of the day
                                or they can be recurring timers that run every hour at a particular minute and second of the hour.
                                <br />
                                <b>Multiple timers</b> are a number of pre-configured timers that you manually start when needed.
                                Optionally you can link these timers so that when one finishes, the other one will start auttomatically.
                                When linking timers, you are only able to set an End Message on the last timer in the chain.
                                It is possible to define both linked and unliked timers in the same configuration.
                                <br /><br />
                                <Radio name="scheduled" value="scheduled" checked={timerType === 'scheduled'} onChange={radioButtonClicked}>
                                    Scheduled Timers<br />
                                </Radio>{' '}
                                <Radio name="multiple" value="multiple" checked={timerType === 'multiple'} onChange={radioButtonClicked}>
                                    Multiple Timers<br />
                                </Radio>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} sm={9} md={7} lg={6}>
                                <div style={{ display: timerType === 'select' ? "none" : "block", paddingRight: "15px" }}>
                                    <Row>
                                        <BootstrapTable data={fixedTimers} selectRow={selectAvailableRowProp} striped hover height='280px'>
                                            <TableHeaderColumn hidden isKey={true} dataField='row'>row</TableHeaderColumn>
                                            <TableHeaderColumn width="10%" dataField='hour' hidden={timerType !== 'scheduled'}>HH</TableHeaderColumn>
                                            <TableHeaderColumn width="10%" dataField='minute' hidden={timerType !== 'scheduled'}>MI</TableHeaderColumn>
                                            <TableHeaderColumn width="10%" dataField='second' hidden={timerType !== 'scheduled'}>SS</TableHeaderColumn>
                                            <TableHeaderColumn width="30%" dataField='startMessage' dataFormat={startMessageFormatter}>Start Msg</TableHeaderColumn>
                                            <TableHeaderColumn width="20%" dataField='endMessage' dataFormat={endMessageFormatter}>End Msg</TableHeaderColumn>
                                            <TableHeaderColumn width="10%" dataField='duration'>Len</TableHeaderColumn>
                                            <TableHeaderColumn width="10%" dataField='toggleDisplay' dataFormat={toggleFormatter}>Mics</TableHeaderColumn>
                                        </BootstrapTable>
                                    </Row>
                                    <Row><br /></Row>
                                    <Row>
                                        <Col xs={1} style={headStyleScheduled}>Hour</Col>
                                        <Col xs={1} style={headStyleScheduled}>Min</Col>
                                        <Col xs={1} style={headStyleScheduled}>Sec</Col>
                                        <Col xs={4} style={headStyle}>Start Message</Col>
                                        <Col xs={2} style={headStyle}>End Msg</Col>
                                        <Col xs={1} style={headStyle}>Len</Col>
                                        <Col xs={1} style={headStyle}>Mics</Col>
                                        <Col xs={3} style={headStyleMultiple}>Run At End</Col>
                                    </Row>
                                    <Row>
                                        <Col xs={1} style={colStyleScheduled}>
                                            <input type="text" style={inputStyle} disabled={editDisabled} className="form-control input-md" name="hour" autoFocus
                                                value={hour} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                        </Col>
                                        <Col xs={1} style={colStyleScheduled}>
                                            <input type="text" style={inputStyle} disabled={editDisabled} className={errorsCalc.minute ? "form-control input-md error" : "form-control input-md"}
                                                name="minute" value={minute} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                        </Col>
                                        <Col xs={1} style={colStyleScheduled}>
                                            <input type="text" style={inputStyle} disabled={editDisabled} className={errorsCalc.second ? "form-control input-md error" : "form-control input-md"}
                                                name="second" value={second} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                        </Col>
                                        <Col xs={4} style={colStyle}>
                                            <input type="text" style={inputStyle} disabled={editDisabled} className={errorsCalc.startMessage ? "form-control input-md error" : "form-control input-md"}
                                                name="startMessage" value={startMessage} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                        </Col>
                                        <Col xs={2} style={colStyle}>
                                            <input type="text" style={inputStyle} disabled={editDisabled} className="form-control input-md"
                                                name="endMessage" value={endMessage} onChange={handleFixedChange} />
                                        </Col>
                                        <Col xs={1} style={colStyle}>
                                            <input type="text" style={inputStyle} disabled={editDisabled} className={errorsCalc.duration ? "form-control input-md error" : "form-control input-md"}
                                                name="duration" value={duration} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                        </Col>
                                        <Col xs={1} style={toggleStyle}>
                                            {' '}<input name="toggle" checked={toggle ? true : false} onChange={handleFixedChange} disabled={editDisabled} type="checkbox" />
                                        </Col>
                                        <Col xs={3} style={colStyleMultiple} className='chainTimerButton'>
                                            <DropdownButton bsStyle={'primary'} title={dropdownTitle}
                                                style={{ width: "100%", whiteSpace: "nowrap", overflowX: "hidden", textOverflow: 'ellipsis' }}
                                                disabled={editDisabled} key={2}
                                                id={"dropdown-timers" + parameters.config.event_screen_module_id}
                                                onSelect={chainedTimerSelected}>
                                                {dropdownTimers}
                                            </DropdownButton>
                                        </Col>
                                        <Col xs={1} style={colActionStyle}>
                                            <RmToolTip itemText="delete" itemType="icon" placement="top" onClick={deleteRow} style={deleteGlyphStyle} toolTipText={<span>Delete<br />Timer</span>} />
                                            <RmToolTip itemText="edit" itemType="icon" placement="top" onClick={editRow} style={editGlyphStyle} toolTipText={<span>Edit<br />Timer</span>} />
                                            <RmToolTip itemText="highlight_off" itemType="icon" placement="top" onClick={undoRow} style={canGlyphStyle} toolTipText={<span>Undo<br />Changes</span>} />
                                            <RmToolTip itemText="done" itemType="icon" placement="top" onClick={saveRow} style={saveGlyphStyle} toolTipText={<span>Save<br />Changes</span>} />
                                            <RmToolTip itemText="add_circle" itemType="icon" placement="top" onClick={addRow} style={addGlyphStyle} toolTipText={<span>Add a<br />new timer</span>} />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <div style={{ color: "red", height: "20px" }}>{message}</div>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </Grid>
                </Modal.Body>
                <Modal.Footer>
                    <Button style={{}} name="cancel" bsStyle="danger"
                        disabled={glyphsCancelVisible || glyphsSaveVisibleCalc}
                        onClick={handleButttonClick}>Exit Configuration</Button>
                </Modal.Footer>
            </Modal>

            <div style={{ height: "100%" }}>
                {/* SIMPLE TIMER */}
                <div style={stdTimerView}>
                    <Title fontSize={headingFontSize + "px"}>
                        <span style={{ paddingLeft: "5px" }}>{timerHeading}</span>
                        <RmToolTip itemText="schedule" itemType="icon" placement="bottom" onClick={showModalFn} style={timeGlyphStyle} toolTipText={<span>Advanced<br />Timers</span>} />
                        <RmToolTip itemText="highlight_off" itemType="icon" placement="bottom" onClick={killTimer} style={clearStyle} toolTipText={<span>Stop a<br />running<br />Timer</span>} />
                    </Title>
                    <Body fontSize={dataRow.fontSize + "px"} height={bodyHeight + "px"}>
                        <div style={{ display: "flex" }}>
                            <input type="text" name="description" style={descriptionFieldStyle} placeholder="Description" value={timerParams.description} onChange={handleChange} onKeyDown={handleTimerKeyDown} />
                            <input type="text" name="secs" style={secondsFieldStyle} placeholder="Secs" value={timerParams.secs} onChange={handleChange} onKeyDown={handleTimerKeyDown} />
                        </div>
                        <div style={{ display: "flex" }}>
                            <input type="text" name="instructions" style={instructionFieldStyle} placeholder="Instructions" value={timerParams.instructions} onChange={handleChange} onKeyDown={handleTimerKeyDown} />
                            <div style={{ flexGrow: 1, width: "30px", marginLeft: "10px", marginRight: "10px" }} />
                        </div>
                        <div style={{ fontSize: (dataRow.fontSize - 2) + "px", display: "flex", lineHeight: dataRow.height + "px" }}>
                            <div style={{ flexGrow: 7, marginLeft: "10px", display: "inline-block" }}>
                                <input name="toggle" style={{ height: dataRow.height + "px", verticalAlign: "bottom", marginRight: "10px" }}
                                    defaultChecked={timerParams.toggle} onChange={handleChange} type="checkbox" />
                                {' '}Toggle Mics
                            </div>
                            <Button name="start" onClick={handleClick} disabled={startButtonDisabled} bsSize="small" style={{ marginLeft: '10px', alignSelf: 'center', borderRadius: '50px', paddingLeft: '14px', paddingRight: '14px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '1.2', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'inherit' }}>Send</Button>
                        </div>
                    </Body>
                </div>

                {/* FIXED TIMER */}
                <div ref={fixedTimerHeadingRowRef} style={fixedTimerView}>
                    <Title fontSize={headingFontSize + "px"}>
                        <span style={{ paddingLeft: "5px" }}>{timerHeading}</span>
                        <RmToolTip itemText="schedule" itemType="icon" placement="bottom" onClick={showModalFn} style={timeGlyphStyle} toolTipText={<span>Advanced<br />Timers</span>} />
                        <RmToolTip itemText="highlight_off" itemType="icon" placement="bottom" onClick={killTimer} style={clearStyle} toolTipText={<span>Stop a<br />running<br />Timer</span>} />
                    </Title>
                    <Body fontSize={dataRow.fontSize + "px"} height={bodyHeight + "px"}>
                        <ul name="logInformation"
                            style={{ marginLeft: "10px", width: "calc(100% - 10px)", paddingRight: "10px", borderStyle: "none", overflowX: "hidden", overflowY: "auto", listStyle: "none", paddingLeft: 0, height: "100%", backgroundColor: parameters.colours.backgroundColor, color: parameters.colours.color }}
                            size="30">
                            {timerType === 'multiple' ? multipleTImerButtonsForDisplay : options}
                        </ul>
                    </Body>
                </div>
            </div>

            <div style={{ position: "fixed", top: "-2000px", left: "-2000px", backgroundColor: "pink", whiteSpace: "pre" }} ref={fontSizeCheckerRef} />
            <LoggingIndicator logStatus={loggingModule} />
        </Tile>
    );
}

function validate(timerType, hour, minute, second, duration, startMessage) {
    let message = "";
    let minuteError = false, secondError = false, durationError = false, startMessageError = false;

    if (duration.length === 0) { durationError = true; message += 'Duration cannot be empty. '; }
    if (startMessage.length === 0) { startMessageError = true; message += 'Start message cannot be empty. '; }

    if (timerType === 'scheduled') {
        if (minute.length === 0) { minuteError = true; message += 'Minute cannot be empty. '; }
        if (second.length === 0) { secondError = true; message += 'Second cannot be empty. '; }
        if (hour === '*' && parseInt(minute, 10) === 0 && parseInt(second, 10) === 0) {
            minuteError = true; secondError = true;
            message += 'Minute and Second cannot be 0 for hourly timers. ';
        }
        if (message.length === 0) {
            if (hour === '*') {
                message = second === "0"
                    ? "Timer will run hourly at " + minute + " minutes exactly past the hour, for " + duration + " seconds"
                    : "Timer will run hourly at " + minute + " minutes and " + second + " seconds past the hour, for " + duration + " seconds";
            } else {
                message = "Timer will run once at " + hour + ":" +
                    (parseInt(minute, 10) < 10 ? "0" + minute : minute) + ":" +
                    (parseInt(second, 10) < 10 ? "0" + second : second) + ", for " + duration + " seconds";
            }
        }
    }

    return { minute: minuteError, second: secondError, duration: durationError, startMessage: startMessageError, message };
}
